import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, xeroLink } from '../api';

type Audience = 'dormant_customers' | 'dormant_referrers';
type Tab = 'campaigns' | 'templates' | 'audiences';

const AUD: Record<Audience, { label: string; days: number; blurb: string }> = {
  dormant_customers: {
    label: 'Dormant customers',
    days: 365,
    blurb: 'Customers last invoiced at least this long ago, with no booking since.',
  },
  dormant_referrers: {
    label: 'Quiet referrers',
    days: 180,
    blurb: 'Active referral partners not contacted within this window.',
  },
};

function fmtDate(ts: string | null) {
  if (!ts) return 'never';
  return new Date(ts).toLocaleDateString('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// Small semantic status badges (distinct solid colours, white text).
const STATUS_COLOR: Record<string, string> = {
  draft: '#8A8A8A',
  scheduled: '#D9B256',
  sending: '#1B365D',
  sent: '#00B097',
  cancelled: 'var(--color-error,#EE2937)',
};

const btn = (bg: string): React.CSSProperties => ({
  background: bg,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  fontWeight: 600,
  cursor: 'pointer',
});
const field: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--color-border,#ccc)',
  borderRadius: 6,
  width: '100%',
};
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border,#e0e0e0)',
  borderRadius: 8,
  padding: 'var(--spacing-3,16px)',
  marginBottom: 12,
  background: 'var(--color-surface,#fff)',
};

// ---------- Audiences preview ----------
function AudiencesTab({ onOpenCampaign }: { onOpenCampaign: (id: number) => void }) {
  const [audience, setAudience] = useState<Audience>('dormant_customers');
  const [days, setDays] = useState(AUD.dormant_customers.days);
  const [maxDays, setMaxDays] = useState<number | null>(null);
  const isCust = audience === 'dormant_customers';
  const { data, isLoading } = useQuery({
    queryKey: ['campaign-audience', audience, days, maxDays],
    queryFn: () =>
      audience === 'dormant_customers' ? api.getDormantCustomers(days, maxDays) : api.getDormantReferrers(days),
  });
  const recipients: any[] = data?.recipients || [];
  const pick = (a: Audience) => {
    setAudience(a);
    setDays(AUD[a].days);
    setMaxDays(null);
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(Object.keys(AUD) as Audience[]).map((a) => (
          <button
            key={a}
            onClick={() => pick(a)}
            style={{
              ...btn(audience === a ? 'var(--color-accent)' : '#fff'),
              color: audience === a ? '#fff' : 'var(--color-text,#222)',
              border: '1px solid var(--color-border,#ccc)',
            }}
          >
            {AUD[a].label}
          </button>
        ))}
      </div>
      <div style={cardStyle}>
        <div style={{ color: 'var(--color-text-muted,#666)', marginBottom: 8 }}>{AUD[audience].blurb}</div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {isCust ? 'Last invoiced between' : 'Not contacted in over'}
          <input
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))}
            style={{ ...field, width: 90 }}
          />
          {isCust && (
            <>
              <span style={{ color: 'var(--color-text-muted,#888)' }}>to</span>
              <input
                type="number"
                min={days}
                placeholder="no limit"
                value={maxDays ?? ''}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setMaxDays(v > 0 ? v : null);
                }}
                style={{ ...field, width: 90 }}
              />
            </>
          )}
          days ago
          {[90, 180, 365, 730].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              style={{
                border: '1px solid var(--color-border,#ccc)',
                borderRadius: 999,
                padding: '2px 10px',
                cursor: 'pointer',
                background: days === d ? 'var(--color-accent-muted)' : 'transparent',
              }}
            >
              {d === 365 ? '1y' : d === 730 ? '2y' : `${d}d`}
            </button>
          ))}
        </label>
      </div>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {isLoading ? 'Building…' : `${recipients.length} match`}
      </div>
      <div style={{ border: '1px solid var(--color-border,#e0e0e0)', borderRadius: 8, overflow: 'hidden' }}>
        {recipients.map((r, i) =>
          audience === 'dormant_customers' ? (
            <DormantCustomerRow key={r.contactId} r={r} first={i === 0} onOpenCampaign={onOpenCampaign} />
          ) : (
            <div
              key={r.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 12px',
                borderTop: i === 0 ? 'none' : '1px solid var(--color-border,#eee)',
              }}
            >
              <span>
                <strong>{r.name || '(no name)'}</strong>{' '}
                <span style={{ color: 'var(--color-text-muted,#666)', fontSize: 12 }}>{r.relationship_type || ''}</span>
              </span>
              <span style={{ fontSize: 12, color: 'var(--color-text-muted,#666)' }}>
                contact {fmtDate(r.last_contacted_at)}
              </span>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

// A dormant customer row: edit the email, toggle email/SMS opt-out (saved to the
// contact), see + open the last campaign they were in, expand invoice/quote
// history, and jump to the last invoice in Xero.
function DormantCustomerRow({
  r,
  first,
  onOpenCampaign,
}: {
  r: any;
  first: boolean;
  onOpenCampaign: (id: number) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(r.email || '');
  const { data: detail, isLoading } = useQuery({
    queryKey: ['xero-contact', r.contactId],
    queryFn: () => api.getXeroContact(r.contactId),
    enabled: open,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['campaign-audience'] });
  const saveEmail = useMutation({
    mutationFn: () => api.updateContact(r.customerId, { email }),
    onSuccess: refresh,
  });
  const toggleOptout = useMutation({
    mutationFn: (v: { channel: 'email' | 'sms'; optedOut: boolean }) =>
      api.setContactOptout(r.customerId, v.channel, v.optedOut),
    onSuccess: refresh,
  });
  const pill = (on: boolean): React.CSSProperties => ({
    border: '1px solid var(--color-border,#ccc)',
    borderRadius: 999,
    padding: '2px 10px',
    fontSize: 12,
    cursor: r.customerId ? 'pointer' : 'not-allowed',
    background: on ? 'var(--color-accent)' : 'var(--color-surface-elevated,#f3f3f3)',
    color: on ? '#fff' : 'var(--color-text-muted,#888)',
  });
  const emailChanged = (email || '') !== (r.email || '');
  return (
    <div style={{ borderTop: first ? 'none' : '1px solid var(--color-border,#eee)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ flex: '1 1 180px', minWidth: 140 }}>
          <strong>{r.name || '(no name)'}</strong>
        </span>
        <input
          value={email}
          placeholder="no email"
          onChange={(e) => setEmail(e.target.value)}
          style={{ ...field, width: 200, padding: '4px 8px', fontSize: 12 }}
        />
        {emailChanged && (
          <button
            onClick={() => saveEmail.mutate()}
            disabled={saveEmail.isPending || !r.customerId}
            style={{ ...pill(true), background: 'var(--color-accent)' }}
          >
            {saveEmail.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
        <button
          title="Email marketing"
          disabled={!r.customerId || toggleOptout.isPending}
          onClick={() => toggleOptout.mutate({ channel: 'email', optedOut: !r.emailOptedOut })}
          style={pill(!r.emailOptedOut)}
        >
          Email {r.emailOptedOut ? 'off' : 'on'}
        </button>
        <button
          title={r.mobile ? 'SMS marketing' : 'No mobile on file'}
          disabled={!r.customerId || !r.mobile || toggleOptout.isPending}
          onClick={() => toggleOptout.mutate({ channel: 'sms', optedOut: !r.smsOptedOut })}
          style={{ ...pill(r.mobile && !r.smsOptedOut), opacity: r.mobile ? 1 : 0.5 }}
        >
          SMS {!r.mobile ? 'n/a' : r.smsOptedOut ? 'off' : 'on'}
        </button>
        <span style={{ fontSize: 12, color: 'var(--color-text-muted,#666)' }}>
          inv {fmtDate(r.lastInvoiceDate)} · {r.daysSince}d
        </span>
        <button
          onClick={() => setOpen((o) => !o)}
          style={{ border: '1px solid var(--color-border,#ccc)', background: '#fff', borderRadius: 6, padding: '3px 9px', fontSize: 12, cursor: 'pointer' }}
        >
          {open ? 'Hide' : 'Details'}
        </button>
        {r.lastInvoiceXeroId && (
          <a
            href={xeroLink.invoice(r.lastInvoiceXeroId)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, fontWeight: 600 }}
          >
            Last invoice {r.lastInvoiceNumber || ''} ↗
          </a>
        )}
      </div>
      <div style={{ padding: '0 12px 8px', fontSize: 12, color: 'var(--color-text-muted,#777)' }}>
        {r.lastCampaignId ? (
          <>
            Last campaign:{' '}
            <button
              onClick={() => onOpenCampaign(r.lastCampaignId)}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
            >
              {r.lastCampaignName}
            </button>{' '}
            {r.lastCampaignSentAt ? `· sent ${fmtDate(r.lastCampaignSentAt)}` : '· not yet sent'}
          </>
        ) : (
          'Last campaign: none'
        )}
      </div>
      {open && (
        <div style={{ padding: '4px 14px 12px', fontSize: 13, background: 'var(--color-surface-elevated,#fafafa)' }}>
          {isLoading && <div style={{ color: 'var(--color-text-muted,#888)' }}>Loading…</div>}
          {detail?.contact && (
            <div style={{ marginBottom: 8, color: 'var(--color-text-muted,#555)' }}>
              {[detail.contact.email, detail.contact.mobile, detail.contact.address].filter(Boolean).join(' · ')}
            </div>
          )}
          {detail && (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Invoices ({detail.invoices.length})</div>
                {detail.invoices.slice(0, 8).map((inv: any) => (
                  <div key={inv.xeroId}>
                    <a href={xeroLink.invoice(inv.xeroId)} target="_blank" rel="noopener noreferrer">
                      {inv.number}
                    </a>{' '}
                    <span style={{ color: 'var(--color-text-muted,#888)' }}>{fmtDate(inv.docDate)} · {inv.status}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Quotes ({detail.quotes.length})</div>
                {detail.quotes.slice(0, 8).map((qq: any) => (
                  <div key={qq.xeroId}>
                    <a href={xeroLink.quote(qq.xeroId)} target="_blank" rel="noopener noreferrer">
                      {qq.number}
                    </a>{' '}
                    <span style={{ color: 'var(--color-text-muted,#888)' }}>{fmtDate(qq.docDate)} · {qq.status}</span>
                  </div>
                ))}
                {detail.quotes.length === 0 && <div style={{ color: 'var(--color-text-muted,#888)' }}>none</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Templates ----------
// Exported so the unified Templates home (Messaging → Templates) can render the
// campaign templates alongside the operational ones.
export function CampaignTemplatesTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['campaign-templates'], queryFn: () => api.getCampaignTemplates() });
  const [editing, setEditing] = useState<any | null>(null);
  const save = useMutation({
    mutationFn: (t: any) => (t.id ? api.updateCampaignTemplate(t.id, t) : api.createCampaignTemplate(t)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-templates'] });
      setEditing(null);
    },
  });
  const del = useMutation({
    mutationFn: (id: number) => api.deleteCampaignTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign-templates'] }),
  });
  const [testTo, setTestTo] = useState('');
  const [testName, setTestName] = useState('');
  const [testMsg, setTestMsg] = useState('');
  // Real customers to impersonate in a test (dormant audience, wide window).
  const { data: dormant } = useQuery({
    queryKey: ['dormant-pick'],
    queryFn: () => api.getDormantCustomers(365),
  });
  const pickList: any[] = dormant?.recipients || [];
  const testMut = useMutation({
    mutationFn: () => api.testCampaignTemplate(editing.id, testTo, testName || undefined),
    onSuccess: (res) =>
      setTestMsg(
        `Sent as "${testName || 'Sam Taylor'}". ` +
          res.map((r) => `${r.to}: ${r.status}${r.error ? ` (${r.error})` : ''}`).join(' · '),
      ),
    onError: (e: any) => setTestMsg(e.message),
  });
  const templates: any[] = data || [];

  if (editing) {
    return (
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit template' : 'New template'}</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <input
            placeholder="Template name"
            style={field}
            value={editing.name || ''}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
          <input
            placeholder="Email subject — supports {{first_name}}"
            style={field}
            value={editing.subject || ''}
            onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
          />
          <textarea
            placeholder={'Write your message as plain text. Use {{first_name}} where the name goes.\nBlank lines start new paragraphs; bullet lines with • are fine.'}
            style={{ ...field, minHeight: 220, fontFamily: 'inherit' }}
            value={editing.body || ''}
            onChange={(e) => setEditing({ ...editing, body: e.target.value })}
          />
          <span style={{ fontSize: 12, color: 'var(--color-text-muted,#888)' }}>
            Plain text is fine, no HTML needed. Tokens: {'{{first_name}}'}, {'{{name}}'}, {'{{business}}'}. We format it
            into the email automatically.
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={btn('var(--color-accent)')}
              disabled={save.isPending || !editing.name?.trim() || !editing.body?.trim()}
              onClick={() => save.mutate(editing)}
            >
              Save
            </button>
            <button style={{ ...btn('#fff'), color: '#333', border: '1px solid var(--color-border,#ccc)' }} onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>

          {editing.id ? (
            <div style={{ marginTop: 8, borderTop: '1px solid var(--color-border,#eee)', paddingTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Send a test</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  style={{ ...field, flex: '1 1 240px' }}
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                >
                  <option value="">Sample name (Sam Taylor)</option>
                  {pickList.map((c) => (
                    <option key={c.contactId} value={c.name || ''}>
                      {c.name || '(no name)'}{c.email ? ` — ${c.email}` : ''}
                    </option>
                  ))}
                </select>
                <input
                  placeholder="send to (your email)"
                  style={{ ...field, flex: '1 1 200px' }}
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                />
                <button
                  style={btn('var(--color-accent)')}
                  disabled={testMut.isPending || !testTo.trim()}
                  onClick={() => { setTestMsg(''); testMut.mutate(); }}
                >
                  {testMut.isPending ? 'Sending…' : 'Send test'}
                </button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted,#888)', marginTop: 4 }}>
                Pick a real customer to fill {'{{first_name}}'} (or leave as the sample), then send the email to your
                own address to preview it. Save first if you've edited. Nothing goes to the customer.
              </div>
              {testMsg && <div style={{ fontSize: 13, marginTop: 6 }}>{testMsg}</div>}
            </div>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--color-text-muted,#888)' }}>Save the template to enable test sends.</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button style={{ ...btn('var(--color-accent)'), marginBottom: 12 }} onClick={() => setEditing({ channel: 'email' })}>
        + New template
      </button>
      {templates.length === 0 && <p style={{ color: 'var(--color-text-muted,#666)' }}>No templates yet.</p>}
      {templates.map((t) => (
        <div key={t.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600 }}>{t.name}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-muted,#666)' }}>{t.subject}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={{ ...btn('#fff'), color: '#333', border: '1px solid var(--color-border,#ccc)' }} onClick={() => setEditing(t)}>
                Edit
              </button>
              <button style={{ ...btn('#fff'), color: 'var(--color-error,#EE2937)', border: '1px solid var(--color-border,#ccc)' }} onClick={() => del.mutate(t.id)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Campaign detail ----------
function CampaignDetail({ id, onChange }: { id: number; onChange: () => void }) {
  const qc = useQueryClient();
  const [scheduledAt, setScheduledAt] = useState('');
  const { data: c } = useQuery({ queryKey: ['campaign', id], queryFn: () => api.getCampaign(id) });
  const { data: stats } = useQuery({ queryKey: ['campaign-stats', id], queryFn: () => api.getCampaignStats(id) });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['campaign', id] });
    qc.invalidateQueries({ queryKey: ['campaign-stats', id] });
    onChange();
  };
  const build = useMutation({ mutationFn: () => api.buildCampaign(id), onSuccess: refresh });
  const schedule = useMutation({
    mutationFn: () => api.scheduleCampaign(id, new Date(scheduledAt).toISOString()),
    onSuccess: refresh,
  });
  const cancel = useMutation({ mutationFn: () => api.cancelCampaign(id), onSuccess: refresh });
  const reopen = useMutation({ mutationFn: () => api.reopenCampaign(id), onSuccess: refresh });
  const send = useMutation({ mutationFn: (dry: boolean) => api.sendCampaign(id, dry), onSuccess: refresh });
  const [testTo, setTestTo] = useState('');
  const [testRecipientId, setTestRecipientId] = useState<number | undefined>(undefined);
  const [testMsg, setTestMsg] = useState('');
  const testSend = useMutation({
    mutationFn: () => api.testCampaignSend(id, testTo, testRecipientId),
    onSuccess: (r) =>
      setTestMsg(
        `Sent as "${r.sampleName ?? '(none)'}". ` +
          r.results.map((x) => `${x.to}: ${x.status}${x.error ? ` (${x.error})` : ''}`).join(' · '),
      ),
    onError: (e: any) => setTestMsg(e.message),
  });

  if (!c) return <p>Loading…</p>;
  const recipients: any[] = c.recipients || [];
  const editable = c.status === 'draft' || c.status === 'scheduled';

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>{c.name}</h3>
        <span style={{ color: STATUS_COLOR[c.status], fontWeight: 600 }}>{c.status}</span>
      </div>
      <div style={{ color: 'var(--color-text-muted,#666)', fontSize: 13, marginBottom: 12 }}>
        {AUD[c.audience as Audience].label} · last {c.threshold_days} days
        {' · 1/min, 8am-6pm'}
        {c.scheduled_at && ` · scheduled ${new Date(c.scheduled_at).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' })}`}
      </div>
      {c.status === 'sending' && stats && (
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--color-accent)', fontWeight: 600 }}>
          Sending… {stats.sent ?? 0} of {stats.total ?? 0} sent
          {typeof stats.total === 'number' && ` (${(stats.total || 0) - (stats.sent || 0) - (stats.skipped || 0) - (stats.unsubscribed || 0)} to go)`}
        </div>
      )}

      {stats && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          {(['total', 'sent', 'failed', 'skipped', 'unsubscribed', 'reactivated'] as const).map((k) => (
            <div key={k} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: k === 'reactivated' ? 'var(--color-accent)' : 'inherit' }}>
                {stats[k] ?? 0}
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted,#666)' }}>{k}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...cardStyle, background: 'var(--color-surface-elevated,#fafafa)' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Send a test</div>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted,#888)', marginBottom: 8 }}>
          Pick a customer from the list, then send their exact email (name and details filled in) to whatever address
          you type. Nothing goes to the customer.
        </div>
        {recipients.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--color-text-muted,#888)' }}>Build the list first to pick a customer.</div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              style={{ ...field, flex: '1 1 240px' }}
              value={testRecipientId ?? ''}
              onChange={(e) => setTestRecipientId(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Pick a customer…</option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name || '(no name)'}{r.email ? ` — ${r.email}` : ''}
                </option>
              ))}
            </select>
            <input
              placeholder="send to (your email)"
              style={{ ...field, flex: '1 1 200px' }}
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <button
              style={btn('var(--color-accent)')}
              disabled={testSend.isPending || !testTo.trim() || !testRecipientId}
              onClick={() => { setTestMsg(''); testSend.mutate(); }}
            >
              {testSend.isPending ? 'Sending…' : 'Send test'}
            </button>
          </div>
        )}
        {testMsg && <div style={{ fontSize: 13, marginTop: 6 }}>{testMsg}</div>}
      </div>

      {editable && (
        <div style={{ ...cardStyle, background: 'var(--color-surface-elevated,#fafafa)' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button style={btn('var(--color-accent)')} disabled={build.isPending} onClick={() => build.mutate()}>
              {recipients.length ? 'Rebuild list' : 'Build list'}
            </button>
            {build.data && (
              <span style={{ fontSize: 13, color: 'var(--color-text-muted,#666)' }}>
                {build.data.recipients} in audience · {build.data.recipients - build.data.withoutEmail - build.data.suppressed} will be emailed
                {build.data.withoutEmail ? ` · ${build.data.withoutEmail} no email` : ''}
                {build.data.suppressed ? ` · ${build.data.suppressed} suppressed` : ''}
              </span>
            )}
          </div>
          {recipients.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input type="datetime-local" style={{ ...field, width: 220 }} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
              <button style={btn('var(--color-accent)')} disabled={!scheduledAt || schedule.isPending} onClick={() => schedule.mutate()}>
                Schedule send
              </button>
              <button style={{ ...btn('#fff'), color: '#333', border: '1px solid var(--color-border,#ccc)' }} disabled={send.isPending} onClick={() => send.mutate(true)}>
                Send test (dry run)
              </button>
              <button style={{ ...btn('#fff'), color: '#333', border: '1px solid var(--color-border,#ccc)' }} onClick={() => cancel.mutate()}>
                Cancel campaign
              </button>
            </div>
          )}
          {send.data && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              Sent {send.data.sent}, failed {send.data.failed}, skipped {send.data.skipped}.
            </div>
          )}
        </div>
      )}

      {c.status === 'cancelled' && (
        <div style={{ ...cardStyle, background: 'var(--color-surface-elevated,#fafafa)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={btn('var(--color-accent)')} disabled={reopen.isPending} onClick={() => reopen.mutate()}>
              {reopen.isPending ? 'Reopening…' : 'Reopen campaign'}
            </button>
            <span style={{ fontSize: 13, color: 'var(--color-text-muted,#666)' }}>
              Returns this campaign to draft (nothing was sent) so you can rebuild and schedule it again.
            </span>
          </div>
        </div>
      )}

      <div style={{ border: '1px solid var(--color-border,#e0e0e0)', borderRadius: 8, overflow: 'hidden', marginTop: 8 }}>
        {recipients.map((r, i) => (
          <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderTop: i === 0 ? 'none' : '1px solid var(--color-border,#eee)' }}>
            <span>
              <strong>{r.name || '(no name)'}</strong> <span style={{ fontSize: 12, color: 'var(--color-text-muted,#666)' }}>{r.email || 'no email'}</span>
            </span>
            <span style={{ fontSize: 12, color: r.status === 'sent' ? 'var(--color-accent)' : r.status === 'failed' ? 'var(--color-error,#EE2937)' : 'var(--color-text-muted,#666)' }}>
              {r.status}
              {r.reactivated_at ? ' · reactivated ✓' : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Campaigns tab ----------
function CampaignsTab({ focus, onConsumeFocus }: { focus?: number | null; onConsumeFocus?: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['campaigns'], queryFn: () => api.getCampaigns() });
  const { data: templates } = useQuery({ queryKey: ['campaign-templates'], queryFn: () => api.getCampaignTemplates() });
  const [selected, setSelected] = useState<number | null>(null);
  // Open a specific campaign when navigated here from the Audiences tab.
  useEffect(() => {
    if (focus) {
      setSelected(focus);
      onConsumeFocus?.();
    }
  }, [focus, onConsumeFocus]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<any>({ audience: 'dormant_customers', threshold_days: 365, channel: 'email' });

  const create = useMutation({
    mutationFn: () => api.createCampaign(form),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      setCreating(false);
      setSelected(c.id);
      setForm({ audience: 'dormant_customers', threshold_days: 365, channel: 'email' });
    },
  });
  // Live preview of who's in the campaign as audience/window change.
  const previewQ = useQuery({
    queryKey: ['campaign-preview', form.audience, form.threshold_days, form.threshold_max_days],
    queryFn: () =>
      form.audience === 'dormant_customers'
        ? api.getDormantCustomers(form.threshold_days, form.threshold_max_days)
        : api.getDormantReferrers(form.threshold_days),
    enabled: creating,
  });
  const preview: any[] = previewQ.data?.recipients || [];
  const isCust = form.audience === 'dormant_customers';
  const willEmail = isCust ? preview.filter((r) => r.email && !r.suppressed).length : preview.length;
  const suppressedN = isCust ? preview.filter((r) => r.suppressed).length : 0;
  const noEmailN = isCust ? preview.filter((r) => !r.email).length : 0;
  // Email preview personalises as a chosen recipient. Default to the first real
  // one; the operator can pick any customer in the audience from the dropdown.
  const [previewName, setPreviewName] = useState('');
  const defaultSample: string | undefined = (isCust ? preview.find((r) => r.email && !r.suppressed) : preview[0])?.name;
  const sampleName = previewName || defaultSample;
  const emailPreviewQ = useQuery({
    queryKey: ['email-preview', form.template_id, sampleName],
    queryFn: () => api.previewCampaignTemplate(Number(form.template_id), sampleName),
    enabled: creating && !!form.template_id,
  });

  const campaigns: any[] = data || [];
  const tpls: any[] = templates || [];

  if (selected) {
    return (
      <div>
        <button style={{ ...btn('#fff'), color: '#333', border: '1px solid var(--color-border,#ccc)', marginBottom: 12 }} onClick={() => setSelected(null)}>
          ← All campaigns
        </button>
        <CampaignDetail id={selected} onChange={() => qc.invalidateQueries({ queryKey: ['campaigns'] })} />
      </div>
    );
  }

  return (
    <div>
      {!creating ? (
        <button style={{ ...btn('var(--color-accent)'), marginBottom: 12 }} onClick={() => setCreating(true)}>
          + New campaign
        </button>
      ) : (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>New campaign</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <input placeholder="Campaign name" style={field} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <label>
              Audience
              <select
                style={field}
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value, threshold_days: AUD[e.target.value as Audience].days })}
              >
                <option value="dormant_customers">Dormant customers</option>
                <option value="dormant_referrers">Quiet referrers</option>
              </select>
            </label>
            <label>
              Last invoiced between (days ago)
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="number"
                  min={1}
                  style={field}
                  value={form.threshold_days}
                  onChange={(e) => setForm({ ...form, threshold_days: Math.max(1, Number(e.target.value) || 1) })}
                />
                <span style={{ color: 'var(--color-text-muted,#888)' }}>to</span>
                <input
                  type="number"
                  min={form.threshold_days}
                  placeholder="no limit"
                  style={field}
                  value={form.threshold_max_days ?? ''}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setForm({ ...form, threshold_max_days: v > 0 ? v : null });
                  }}
                />
              </div>
            </label>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted,#888)' }}>
              Leave the second box blank for "at least N days". Set both to target a band, e.g. 365 to 400 days since last invoice.
              Sends are paced automatically: one email per minute, only between 8am and 6pm Brisbane time.
            </div>
            <label>
              Template
              <select style={field} value={form.template_id || ''} onChange={(e) => setForm({ ...form, template_id: Number(e.target.value) || null })}>
                <option value="">— choose a template —</option>
                {tpls.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btn('var(--color-accent)')} disabled={create.isPending || !form.name?.trim()} onClick={() => create.mutate()}>
                Create
              </button>
              <button style={{ ...btn('#fff'), color: '#333', border: '1px solid var(--color-border,#ccc)' }} onClick={() => setCreating(false)}>
                Cancel
              </button>
            </div>
            {tpls.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-error,#EE2937)' }}>Tip: create a template first (Templates tab).</div>}

            <div style={{ marginTop: 8, borderTop: '1px solid var(--color-border,#eee)', paddingTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Preview {previewQ.isFetching ? '(loading…)' : `(${preview.length} in audience)`}
              </div>
              {isCust && (
                <div style={{ fontSize: 13, color: 'var(--color-text-muted,#666)', marginBottom: 8 }}>
                  {willEmail} will be emailed
                  {suppressedN ? ` · ${suppressedN} suppressed` : ''}
                  {noEmailN ? ` · ${noEmailN} no email` : ''}
                </div>
              )}
              <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--color-border,#eee)', borderRadius: 8 }}>
                {preview.map((r, i) => {
                  const tag = !isCust
                    ? null
                    : r.suppressed
                      ? { t: 'suppressed', c: 'var(--color-error,#EE2937)' }
                      : !r.email
                        ? { t: 'no email', c: 'var(--color-text-muted,#999)' }
                        : { t: 'will email', c: 'var(--color-accent)' };
                  return (
                    <div
                      key={r.contactId || r.id || i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 10,
                        padding: '6px 10px',
                        borderTop: i === 0 ? 'none' : '1px solid var(--color-border,#f0f0f0)',
                        fontSize: 13,
                      }}
                    >
                      <span>
                        <strong>{r.name || '(no name)'}</strong>{' '}
                        <span style={{ color: 'var(--color-text-muted,#888)' }}>
                          {isCust ? r.email || 'no email' : r.relationship_type || ''}
                        </span>
                      </span>
                      {tag && <span style={{ color: tag.c, fontSize: 12 }}>{tag.t}</span>}
                    </div>
                  );
                })}
                {!previewQ.isFetching && preview.length === 0 && (
                  <div style={{ padding: 10, color: 'var(--color-text-muted,#888)' }}>No one matches at this window.</div>
                )}
              </div>
            </div>

            <div style={{ marginTop: 8, borderTop: '1px solid var(--color-border,#eee)', paddingTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 600 }}>Email preview</div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted,#666)', display: 'flex', gap: 6, alignItems: 'center' }}>
                  Preview as
                  <select
                    style={{ ...field, width: 'auto', padding: '4px 8px' }}
                    value={previewName}
                    onChange={(e) => setPreviewName(e.target.value)}
                  >
                    <option value="">{defaultSample ? `${defaultSample} (first recipient)` : 'first recipient'}</option>
                    {preview
                      .filter((r) => r.name && (!isCust || r.email))
                      .map((r, i) => (
                        <option key={r.contactId || r.id || i} value={r.name}>
                          {r.name}
                          {isCust && r.email ? ` — ${r.email}` : ''}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              {!form.template_id ? (
                <div style={{ fontSize: 13, color: 'var(--color-text-muted,#888)' }}>Choose a template to preview the email.</div>
              ) : emailPreviewQ.isFetching ? (
                <div style={{ fontSize: 13, color: 'var(--color-text-muted,#888)' }}>Loading…</div>
              ) : emailPreviewQ.data ? (
                <div style={{ border: '1px solid var(--color-border,#eee)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--color-border,#eee)', background: 'var(--color-surface-elevated,#fafafa)' }}>
                    <span style={{ color: 'var(--color-text-muted,#888)', fontSize: 12 }}>Subject: </span>
                    <strong>{emailPreviewQ.data.subject}</strong>
                  </div>
                  <iframe
                    title="email preview"
                    srcDoc={emailPreviewQ.data.html}
                    style={{ width: '100%', height: 340, border: 'none', background: '#fff' }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {campaigns.length === 0 && <p style={{ color: 'var(--color-text-muted,#666)' }}>No campaigns yet.</p>}
      {campaigns.map((c) => (
        <div key={c.id} style={{ ...cardStyle, cursor: 'pointer' }} onClick={() => setSelected(c.id)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600 }}>
                {c.name} <span style={{ color: STATUS_COLOR[c.status], fontSize: 13 }}>· {c.status}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted,#666)' }}>
                {AUD[c.audience as Audience]?.label} · {c.recipients} recipients · {c.sent} sent · {c.reactivated} reactivated
              </div>
            </div>
            <span style={{ color: 'var(--color-text-muted,#888)' }}>›</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Shows that the evergreen dormant win-back is active, its schedule, and who it
// will email on the next run — so the automation is visible, not a black box.
function AutoWinbackCard() {
  const { data } = useQuery({ queryKey: ['auto-winback-preview'], queryFn: () => api.getAutoWinbackPreview() });
  const candidates = data?.candidates ?? [];
  return (
    <div style={{ border: '1px solid var(--color-border,#ccc)', borderRadius: 10, padding: 'var(--spacing-3,16px)', marginBottom: 16, background: 'var(--color-surface-elevated, #f7fbfa)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700 }}>🔁 Auto win-back (dormant customers)</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-navy,#1B365D)', background: 'var(--color-gold,#FEC553)', borderRadius: 999, padding: '2px 8px' }}>ACTIVE</span>
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 'var(--font-size-sm,13px)', color: 'var(--color-text-muted,#666)' }}>
        Runs automatically <strong>Tue, Wed &amp; Thu at 8:00am</strong> Brisbane. Emails customers who have newly passed 18 months since their last
        invoice and were never sent a win-back. Deduped so no one is emailed twice, opt-outs skipped, up to <strong>30/day</strong>.
        Edit the wording under <strong>Messaging → Templates → Campaign templates</strong> (“Win-back (dormant customers)” / “… SMS”).
      </p>
      <p style={{ margin: '8px 0 0', fontSize: 'var(--font-size-sm,13px)' }}>
        <strong>{candidates.length}</strong> customer{candidates.length === 1 ? '' : 's'} queued for the next run
        {candidates.length > 0 && (
          <span style={{ color: 'var(--color-text-muted,#666)' }}>
            {': '}
            {candidates.slice(0, 5).map((c) => c.name || c.email).filter(Boolean).join(', ')}
            {candidates.length > 5 ? `, +${candidates.length - 5} more` : ''}
          </span>
        )}
      </p>
    </div>
  );
}

export function Campaigns({ embedded = false }: { embedded?: boolean } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('campaigns');
  // Lets the Audiences tab (and a deep link from a contact) jump straight to a
  // specific campaign.
  const [focusCampaign, setFocusCampaign] = useState<number | null>(null);
  // Open a campaign when arriving via /campaigns?open=<id> (from a contact).
  useEffect(() => {
    const open = searchParams.get('open');
    if (open) {
      setFocusCampaign(Number(open));
      setTab('campaigns');
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, []);
  const tabBtn = (t: Tab, label: string): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid var(--color-border,#ccc)',
    cursor: 'pointer',
    fontWeight: 600,
    background: tab === t ? 'var(--color-accent)' : 'var(--color-surface,#fff)',
    color: tab === t ? '#fff' : 'var(--color-text,#222)',
  });
  return (
    <div style={embedded ? undefined : { maxWidth: 920, margin: '0 auto', padding: 'var(--spacing-3,16px)' }}>
      {!embedded && <h2 style={{ marginTop: 0 }}>Reactivation campaigns</h2>}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button style={tabBtn('campaigns', 'Campaigns')} onClick={() => setTab('campaigns')}>Campaigns</button>
        <button style={tabBtn('audiences', 'Audiences')} onClick={() => setTab('audiences')}>Preview audiences</button>
      </div>
      {tab === 'campaigns' && <AutoWinbackCard />}
      {tab === 'campaigns' && <CampaignsTab focus={focusCampaign} onConsumeFocus={() => setFocusCampaign(null)} />}
      {tab === 'audiences' && (
        <AudiencesTab
          onOpenCampaign={(id: number) => {
            setFocusCampaign(id);
            setTab('campaigns');
          }}
        />
      )}
    </div>
  );
}
